import "./SnapshotChart.scss";
import React, { useMemo, useCallback, useRef } from "react";
import * as R from "ramda";
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
  LabelList,
  ResponsiveContainer,
  ErrorBar,
  Tooltip,
} from "recharts";
import cn from "classnames";
import { DownloadDropdown, Dropdown, DropdownToggleType, ToggleSwitch } from "../../../Components";
import ChartContainer from "../../../Components/ChartContainer";
import { colorMap as colors } from "../../../utils/colors";
import { downloadPNG, exportToExcel } from "../../../utils/download-utils";
import { formatNumberAsInt } from "../../../utils/format-utils";
import { StateSetter } from "../../../utils/types";
import { MdOutlineWarningAmber } from "react-icons/md";
import ReactDOM from "react-dom";

const BAR_CATEGORY_GAP = 6;
const DEFAULT_BAR_CHART_CONTAINER_HEIGHT = 57;
const TOOLTIP_MARGINS = { left: 0, right: 50 }; // same as BarChart margin

const DEFAULT_FORMATTER = (value: number): string => formatNumberAsInt(value);

export interface DataItem {
  label: string | { top: string; bottom: string };
  label2?: string | { top: string; bottom: string };
  name: string;
  value: number;
  value2?: number;
  computedValue?: (value: number, value2?: number) => string;
  incrementality?: boolean;
  added?: boolean;
  /** errorX array for Recharts ErrorBar ([lowerOffset, upperOffset]) */
  errorX?: [number, number];
}

interface DropdownOption {
  value: string;
}

interface LegendProps {
  name: string;
  value: number;
  value2: number | undefined;
  computedValue?: (value: number, value2?: number) => string;
  formatter: (value: number) => string;
  incrementality?: boolean;
  expanded?: boolean;
  label?: number | string;
  label2?: number | string;
  actualValues?: boolean;
  onClick?: () => void;
}

export const Legend: React.FC<LegendProps> = ({
  name,
  value,
  value2,
  computedValue,
  label2,
  label,
  formatter,
  incrementality,
  expanded,
  actualValues = false,
  onClick,
}) => {
  return (
    <div
      className={cn("legend", { hasComputedValue: Boolean(computedValue) })}
      onClick={onClick ? () => onClick() : undefined}
    >
      <div className="itemLabel">
        <div>
          {name}
          {incrementality && (
            <MdOutlineWarningAmber style={{ color: "orange", marginLeft: "5px" }} />
          )}
        </div>
      </div>
      {computedValue && (
        <div className="itemComputedValue">
          <div>{computedValue(value, value2)}</div>
        </div>
      )}
      {expanded ? (
        <div className="itemValueWrapper">
          <div className="itemValue">
            <div className="itemNameAndPercent">
              <span>Spend:</span>
              <span className="legendPercentSpan">{formatter(value)}</span>
            </div>
            {actualValues && <span className="actualValuesSpan">{label}</span>}
          </div>
          {value2 !== undefined && (
            <div className="itemValue">
              <div className="itemNameAndPercent">
                <span>Effect:</span>
                <span className="legendPercentSpan">{formatter(value2)}</span>
              </div>
              {actualValues && <span className="actualValuesSpan">{label2}</span>}
            </div>
          )}
        </div>
      ) : (
        <div className="itemValueWrapper">
          <div className="itemValue">{formatter(value)}</div>
          {value2 !== undefined && <div className="itemValue">{formatter(value2)}</div>}
        </div>
      )}
    </div>
  );
};

interface SnapshotChartProps {
  colorOverrides?: { [key: string]: string };
  data: DataItem[];
  doubleBarChart?: boolean;
  dropdownOptions?: readonly DropdownOption[];
  dropdownValue?: string;
  header?: string;
  setDropdownValue?: (value: string) => void;
  setToggleLabel?: StateSetter<boolean>;
  title: string;
  toggleLabel?: boolean;
  toggleSwitchLabel?: string;
  valueFormatter?: (value: number) => string;
  additionalRightActions?: React.ReactNode;
  exportFormattedData?: any[];
  showDownload?: boolean;
  enableHoverDesign?: boolean;
  barChartContainerHeight?: number;
  /** whether to render confidence interval whiskers */
  showConfidenceIntervals?: boolean;
  enableTooltip?: boolean;
  pngDownloadClassName?: string;
}

export const SnapshotChart: React.FC<SnapshotChartProps> = React.memo(
  ({
    colorOverrides,
    data,
    doubleBarChart,
    dropdownOptions,
    dropdownValue,
    header,
    setDropdownValue,
    setToggleLabel,
    title,
    toggleLabel,
    toggleSwitchLabel,
    valueFormatter = DEFAULT_FORMATTER,
    additionalRightActions,
    exportFormattedData,
    showDownload = true,
    enableHoverDesign = true,
    barChartContainerHeight = DEFAULT_BAR_CHART_CONTAINER_HEIGHT,
    showConfidenceIntervals = false,
    enableTooltip = false,
    pngDownloadClassName,
  }) => {
    const excelDownload = useCallback(() => {
      const dataToExport = exportFormattedData ? exportFormattedData : data;
      const resolvedTitle = title ? title.slice(0, 30) : "snapshot";
      exportToExcel(dataToExport, `${resolvedTitle.replace(/ /g, "_")}`);
    }, [title, data, exportFormattedData]);

    const pngDownload = useCallback(async () => {
      await downloadPNG(pngDownloadClassName || ".snapshotGraphContents", title);
    }, [title, pngDownloadClassName]);

    const colorMap = useMemo(() => {
      if (!R.isEmpty(data)) {
        const colorsOriginal = colors(data);

        return { ...colorsOriginal, ...colorOverrides };
      }
    }, [colorOverrides, data]);

    const renderCustomizedLabel = useCallback(
      props => {
        const { x, y, width, value } = props;
        const newWidth = isNaN(width) ? 0 : width;
        const splitValue = value?.toString().split(" ");
        if (!splitValue) {
          return null;
        }
        let val1: string;
        let val2: string;
        if (typeof value === "string") {
          val1 = `${splitValue[0]}${splitValue[1] === "x" ? " x" : ""}`;
          val2 = `${
            splitValue[1] === "x" ? splitValue.slice(2).join(" ") : splitValue.slice(1).join(" ")
          }`;
        } else {
          val1 = value.top;
          val2 = value.bottom;
        }
        const showSecondLabel = !doubleBarChart || toggleLabel;

        return (
          <g>
            <text
              className="snapshotBarLabel"
              x={x + newWidth + 4}
              y={showSecondLabel ? y + 5 : y + 11}
              fill="#1f003f"
              stroke="#1f003f"
              strokeWidth={0}
              textAnchor="start"
              fontWeight={400}
              width={20}
              dominantBaseline="middle"
            >
              {val1}
            </text>
            {showSecondLabel && (
              <text
                className="snapshotBarLabel"
                x={x + newWidth + 4}
                y={y + 17}
                fill="#1f003f"
                stroke="#1f003f"
                strokeWidth={0}
                textAnchor="start"
                fontWeight={400}
                width={20}
                dominantBaseline="middle"
              >
                {val2}
              </text>
            )}
          </g>
        );
      },
      [toggleLabel, doubleBarChart]
    );

    const barContainerRef = useRef<HTMLDivElement>(null);
    const PortalTooltip = ({ payload, label }) => {
      if (!payload || payload.length === 0) {
        return null;
      }

      const item = payload[0].payload as DataItem;
      const rect = barContainerRef.current!.getBoundingClientRect();
      const chartW = rect.width;
      const chartH = rect.height;

      // 1️⃣ X: value → pixel
      const maxValue = Math.max(...data.map(d => d.value));
      const innerW = chartW - TOOLTIP_MARGINS.left - TOOLTIP_MARGINS.right;
      const xSvg = (item.value / maxValue) * innerW + TOOLTIP_MARGINS.left;

      // 2️⃣ Y: row index → center pixel
      const idx = data.findIndex(d => d.name === item.name);
      const rowH = (chartH - (data.length - 1) * BAR_CATEGORY_GAP) / data.length;
      const ySvg = idx * (rowH + BAR_CATEGORY_GAP) + rowH / 2;

      // 3️⃣ convert to page coords
      const xPage = rect.left + window.pageXOffset + xSvg;
      const yPage = rect.top + window.pageYOffset + ySvg;

      // 4️⃣ style + clamp
      const TOOLTIP_W = 200,
        TOOLTIP_H = 100;
      const style: React.CSSProperties = {
        position: "absolute",
        pointerEvents: "none",
        zIndex: 9999,
        left: Math.min(xPage, window.innerWidth - TOOLTIP_W - 10),
        top: Math.max(10, yPage - TOOLTIP_H - 10),
      };

      let content: any;
      if (!payload || !(payload && payload[0]?.payload) || !label) {
        content = null;
      } else {
        const { name, value, valueLower, valueUpper } = payload[0].payload;
        content = (
          <div className="snapshotTooltip" style={style}>
            <div className="headerLabel">{name}</div>
            <div className="itemList">
              {name !== "Baseline" && (
                <div className="itemRow">
                  <div className="whiskers">
                    <div className="line"></div>
                  </div>
                  <div className="name">Confidence Interval: </div>
                  <div className="value">
                    {`${valueFormatter(valueLower)}-${valueFormatter(valueUpper)}`}
                  </div>
                </div>
              )}
              <div className="itemRow">
                <div className="square" style={{ backgroundColor: colorMap[name] }} />
                <div className="name">{name === "Baseline" ? "Baseline: " : "Estimate: "}</div>
                <div className="value">{valueFormatter(value)}</div>
              </div>
            </div>
          </div>
        );
      }
      return ReactDOM.createPortal(content, document.body);
    };
    return (
      <>
        <div className="snapshotChartContainer">
          <ChartContainer
            enableHoverDesign={enableHoverDesign}
            title={title}
            rightActions={
              <>
                {additionalRightActions}
                {!R.isNil(toggleLabel) && setToggleLabel && toggleSwitchLabel && (
                  <ToggleSwitch
                    label={toggleSwitchLabel}
                    checked={toggleLabel}
                    onChange={setToggleLabel}
                  />
                )}
                {dropdownOptions && dropdownValue && setDropdownValue && (
                  <Dropdown
                    type={DropdownToggleType.FILLED}
                    design="secondary"
                    size="sm"
                    value={dropdownValue}
                    options={dropdownOptions}
                    onChange={setDropdownValue}
                  />
                )}
                {showDownload && (
                  <DownloadDropdown size="sm" onClickOptions={[excelDownload, pngDownload]} />
                )}
              </>
            }
          >
            <div id="snapshotGraphContents" className="snapshotGraphContents">
              {header && <div className="snapshotGraphHeader">{header}</div>}
              <div
                className="barChartsContainer"
                style={{ height: data.length * barChartContainerHeight }}
              >
                <div className="leftOfChart">
                  {data.map(item => (
                    <Legend
                      name={item.name}
                      value={item.value}
                      value2={item.value2}
                      computedValue={item.computedValue}
                      formatter={valueFormatter}
                      key={item.name}
                      incrementality={item.incrementality}
                    />
                  ))}
                </div>
                <div
                  className="snapshotBarContainer"
                  ref={barContainerRef}
                  style={{ height: data.length * barChartContainerHeight }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      barCategoryGap={BAR_CATEGORY_GAP}
                      data={data}
                      layout="vertical"
                      margin={{ top: 0, right: 50, bottom: 0, left: 0 }}
                    >
                      <XAxis type="number" domain={[0, "dataMax"]} hide />
                      <YAxis type="category" dataKey="name" hide />
                      <Bar
                        dataKey="value"
                        isAnimationActive={false}
                        opacity={doubleBarChart ? 0.5 : 1}
                        strokeWidth={2}
                        radius={title === "Spend and Effect Share" ? 0 : 2}
                        strokeOpacity={1}
                      >
                        {data.map(item => (
                          <Cell key={`${item.name}value1`} fill={colorMap[item.name]} />
                        ))}
                        {showConfidenceIntervals && <ErrorBar dataKey="errorX" strokeWidth={2} />}
                        {(toggleLabel || doubleBarChart) && (
                          <LabelList dataKey="label" content={renderCustomizedLabel} />
                        )}
                      </Bar>
                      {enableTooltip && (
                        <Tooltip
                          content={({ label, payload }) => {
                            return <PortalTooltip payload={payload} label={label} />;
                          }}
                          isAnimationActive={false}
                          cursor={{ fill: "none" }}
                        />
                      )}
                      {doubleBarChart && (
                        <>
                          <Bar dataKey="value2" isAnimationActive={false} stackId="revenue">
                            {data.map(item => (
                              <Cell key={`${item.name}value2`} fill={colorMap[item.name]} />
                            ))}
                            {(toggleLabel || doubleBarChart) && (
                              <LabelList dataKey="label2" content={renderCustomizedLabel} />
                            )}
                          </Bar>
                        </>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </ChartContainer>
        </div>
      </>
    );
  }
);
